import { CircularProgress, Tab, Tabs } from "@mui/material";
import { CorporateContext } from "contexts/CorporateContext/CorporateContext";
import { MillDashboardContextProvider } from "contexts/MillDashboardsContext/MillDashboardContextProvider";
import { LocationsEntity } from "entities/Locations/LocationsEntity";
import { SyntheticEvent, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useStyles } from "./Locations.styles";
import { ProductionLines } from "../CorporateSetupDashboard/ProductionLines";
import { Clusters } from "../CorporateSetupDashboard/Clusters/Clusters.view";
import MillDashboards from "./MillDashboards";
import MillUser from "./MillUser/MillUser.view";
import MillDetails from "./MillDetails/MillDetails.view";
import { NoMillSelected } from "./NoMillSelected/NoMillSelected.view";
import { SummaryReportDashboardContextProvider } from "contexts/SummaryReportDashboardContext/SummaryReportDashboardContextProvider";
import { MillDetailsReportContextProvider } from "contexts/MillDetailsReportContext/MillDetailsReportContextProvider";
import LocationsLayout from "./LocationsLayout/LocationsLayout.view";
import { TrendReportContextProvider } from "contexts/TrendReportContext/TrendReportContextProvider";
import { MillsEntity } from "entities/Mills/MillsEntity";
import { LocationsContext } from "contexts/LocationsContext/LocationsContext";
import { MillLocalserverMetrics } from "./MillLocalserverMetrics/MillLocalserverMetrics";
import { HASH_TO_TAB, TAB_TO_HASH, TabsEnum } from "./LocationsInterfaces";
import { CORPORATE_BACKOFFICE_PATHS } from "constants/constants";
import { MillLocalserverLiveStatusProvider } from "contexts/MillLocalserverLiveStatusContext/MillLocalserverLiveStatusProvider";

const Locations = () => {
    const classes = useStyles();
    const [selectedTab, setSelectedTab] = useState(TabsEnum.dashboards);
    const { isFullscreenActive } = useContext(CorporateContext);
    const { millId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { loading, mill } = LocationsEntity.GetMillLocations(Number(millId));
    const { loading: millTimezoneLoading, millTimezone } = MillsEntity.GetMillTimezone(Number(millId));
    const { handleMillTimezone } = useContext(LocationsContext);

    const isClusterAllowed = useMemo(() => (mill ? mill?.cluster_enabled : false), [mill]);
    const hasLocalServer = useMemo(() => (mill ? mill?.dedicated_localserver : false), [mill]);

    useEffect(() => {
        if (!millTimezoneLoading) {
            handleMillTimezone(millTimezone);
        }
    }, [millTimezone, handleMillTimezone, millTimezoneLoading]);

    useEffect(() => {
        const hash = location.hash.replace("#", "");
        const DEFAULT_TAB = CORPORATE_BACKOFFICE_PATHS.LOCATIONS.HASH_PATHS.DASHBOARDS;

        const tabPermissions = {
            [CORPORATE_BACKOFFICE_PATHS.LOCATIONS.HASH_PATHS.CLUSTERS]: isClusterAllowed,
            [CORPORATE_BACKOFFICE_PATHS.LOCATIONS.HASH_PATHS.LOCAL_SERVER]: hasLocalServer,
        };

        if (!hash) {
            navigate(`#${DEFAULT_TAB}`, { replace: true });
            return;
        }

        if (HASH_TO_TAB[hash] === undefined) {
            navigate(`#${DEFAULT_TAB}`, { replace: true });
            return;
        }

        const requiresPermission = hash in tabPermissions;
        const hasPermission = requiresPermission ? tabPermissions[hash] : true;

        if (requiresPermission && !mill?.mill_id) {
            return;
        }

        if (hasPermission) {
            setSelectedTab(HASH_TO_TAB[hash]);
        } else {
            navigate(`#${DEFAULT_TAB}`, { replace: true });
        }
    }, [location.hash, navigate, isClusterAllowed, hasLocalServer, mill]);

    const tabs = [
        {
            id: TabsEnum.dashboards,
            label: t("locations.dashboards"),
        },
        {
            id: TabsEnum.productionLines,
            label: t("locations.production_lines"),
        },
        {
            id: TabsEnum.clusters,
            label: t("locations.clusters"),
        },
        {
            id: TabsEnum.users,
            label: t("locations.users"),
        },
        {
            id: TabsEnum.details,
            label: t("locations.settings"),
        },
        {
            id: TabsEnum.localserverMetrics,
            label: t("locations.local_server"),
        },
    ];

    const handleTabChange = (_: SyntheticEvent<Element, Event>, newValue: TabsEnum) => {
        setSelectedTab(newValue);
        navigate(`#${TAB_TO_HASH[newValue]}`);
    };

    const tabsComponents = {
        [TabsEnum.dashboards]: {
            component: (
                <MillDashboardContextProvider>
                    <SummaryReportDashboardContextProvider>
                        <MillDetailsReportContextProvider>
                            <TrendReportContextProvider>
                                <MillDashboards />
                            </TrendReportContextProvider>
                        </MillDetailsReportContextProvider>
                    </SummaryReportDashboardContextProvider>
                </MillDashboardContextProvider>
            ),
        },
        [TabsEnum.productionLines]: {
            component: <ProductionLines />,
        },
        [TabsEnum.clusters]: {
            component: <Clusters />,
        },
        [TabsEnum.users]: {
            component: <MillUser clusterAllowed={isClusterAllowed} />,
        },
        [TabsEnum.details]: {
            component: <MillDetails />,
        },
        [TabsEnum.localserverMetrics]: {
            component: <MillLocalserverMetrics />,
        },
    };

    if (loading && !mill?.mill_id)
        return (
            <LocationsLayout headerTitle={t("locations.mill_dashboard_title")}>
                <div className={classes.mainWrapper}>
                    <div className={classes.mainWrapperLoading}>
                        <CircularProgress color="info" />
                        <div className={classes.textLoading}>{t("locations.loading_location_data")}</div>
                    </div>
                </div>
            </LocationsLayout>
        );

    if (!mill?.mill_id)
        return (
            <LocationsLayout headerTitle={`${t("locations.mill_dashboard_title")}`}>
                <div className={classes.mainWrapper}>
                    <div className={classes.mainWrapperLoading}>
                        <NoMillSelected title={t("locations.no_mill_selected")} />
                    </div>
                </div>
            </LocationsLayout>
        );

    return (
        <MillLocalserverLiveStatusProvider>
            <LocationsLayout headerTitle={`${t("locations.mill_dashboard_title")} - ${mill?.name}`}>
                <div className={classes.mainWrapper}>
                    {!isFullscreenActive && (
                        <Tabs className={classes.tabs} value={selectedTab} onChange={handleTabChange}>
                            <Tab value={TabsEnum.dashboards} label={tabs[TabsEnum.dashboards].label} />
                            <Tab value={TabsEnum.productionLines} label={tabs[TabsEnum.productionLines].label} />
                            {isClusterAllowed && (
                                <Tab value={TabsEnum.clusters} label={tabs[TabsEnum.clusters].label} />
                            )}
                            <Tab value={TabsEnum.users} label={tabs[TabsEnum.users].label} />
                            <Tab value={TabsEnum.details} label={tabs[TabsEnum.details].label} />
                            {hasLocalServer && (
                                <Tab
                                    value={TabsEnum.localserverMetrics}
                                    label={tabs[TabsEnum.localserverMetrics].label}
                                />
                            )}
                        </Tabs>
                    )}
                    <div
                        className={`${
                            isFullscreenActive ? classes.tabComponentWrapperFullscreen : classes.tabComponentWrapper
                        }`}
                    >
                        {
                            // @ts-ignore
                            tabsComponents[selectedTab].component
                        }
                    </div>
                </div>
            </LocationsLayout>
        </MillLocalserverLiveStatusProvider>
    );
};

export default Locations;
